import { getSupabase } from "../../lib/supabase";
import { isFastConnector, sortPlugGroups, type ConnectorType } from "./connectors";

export { isFastConnector, type ConnectorType };

export type StationStatus = "available" | "waiting" | "offline" | "unknown";

export type StationCreate = {
  name: string;
  lat: number;
  lng: number;
  address?: string | null;
  photo_url?: string | null;
  pricing_info?: PricingInfo | null;
};

export type StationOut = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  photo_url: string | null;
  photo_status: "pending" | "approved" | "rejected" | null;
  pricing_info: PricingInfo | null;
  owner_id: string | null;
  verified: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateStationResponse = {
  created: StationOut | null;
  candidates: StationOut[];
};

type StationRow = {
  id: string;
  name: string;
  location: string | { type: "Point"; coordinates: [number, number] } | null;
  geohash6: string | null;
  address: string | null;
  photo_url: string | null;
  photo_status: "pending" | "approved" | "rejected" | null;
  pricing_info: PricingInfo | null;
  company: string | null;
  // Detail-only: getById selects it; the stations_nearby RPC does not return it.
  payment_note?: string | null;
  verified: boolean;
  tags: string[];
};

export type PlugSummary = {
  connector_type: ConnectorType;
  // Max kW across chargers of this type at the station — peak capability is
  // what drivers care about on a quick scan, not the slowest unit.
  power_kw: number;
};

export type StationListItem = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  company: string | null;
  status: StationStatus;
  chargerCount: number;
  availableCount: number;
  fastChargerCount: number;
  plugs: PlugSummary[];
  tags: string[];
  // Max(report.created_at, check_in.checked_in_at) — null when neither exists.
  // Drives the freshness subtitle ("Confirmed N min ago" / "No recent reports").
  lastActivityAt: string | null;
};

export type ChargerStatus = "available" | "charging" | "waiting" | "offline" | "unknown";

type ChargerStatusRow = {
  id: string;
  station_id: string;
  status: ChargerStatus;
  connector_type: ConnectorType;
  power_kw: number;
};

export type ChargerInfo = {
  connector_type: ConnectorType;
  power_kw: number;
  status: ChargerStatus;
};

type SingleResult = { data: unknown; error: unknown };

type SupabaseLike = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<SingleResult>;
  from: (table: string) => {
    select: (cols: string) => {
      in: (col: string, ids: string[]) => Promise<SingleResult>;
      eq: (col: string, val: string) => {
        maybeSingle: () => Promise<SingleResult>;
        order: (col: string, opts: { ascending: boolean }) => {
          limit: (n: number) => Promise<SingleResult>;
        };
      };
    };
  };
};

export type StationDetail = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  photoUrl: string | null;
  photoStatus: "pending" | "approved" | "rejected" | null;
  pricingInfo: PricingInfo | null;
  company: string | null;
  paymentNote: string | null;
  verified: boolean;
  tags: string[];
  chargers: ChargerInfo[];
  status: StationStatus;
  lastActivityAt: string | null;
};

export type PricingInfo = {
  type: "free" | "paid";
  price_per_kwh?: number | null;
  currency?: "COP" | null;
};

export async function listByCells(
  cells: string[],
  tag: string = "",
): Promise<StationListItem[]> {
  if (cells.length === 0 && tag.length === 0) return [];
  // Cast through unknown until `supabase gen types` populates `Database`.
  const supabase = getSupabase() as unknown as SupabaseLike;

  // "" = no filter (SQL: length(btrim(p_tag))>0). Hook trims before keying — don't trim here.
  const stationsResp = await supabase.rpc("stations_nearby", {
    p_cells: cells,
    p_tag: tag,
  });
  if (stationsResp.error) {
    throw new Error("stations_nearby RPC failed", { cause: stationsResp.error });
  }
  const rows = (stationsResp.data ?? []) as StationRow[];
  const stationIds = rows.map((r) => r.id);

  // Charger status + last activity are both best-effort: a view/RPC outage
  // should not blank the map. Run them in parallel since they're independent.
  const [chargers, lastActivity] = await Promise.all([
    fetchChargers(supabase, stationIds),
    fetchLastActivity(supabase, stationIds),
  ]);

  const byStation = new Map<string, ChargerStatusRow[]>();
  for (const c of chargers) {
    const arr = byStation.get(c.station_id) ?? [];
    arr.push(c);
    byStation.set(c.station_id, arr);
  }

  const items: StationListItem[] = [];
  for (const row of rows) {
    const point = parseLocation(row.location);
    if (point === null) {
      console.warn("dropping station with unparseable location", {
        station_id: row.id,
        location: row.location,
      });
      continue;
    }
    const stationChargers = byStation.get(row.id) ?? [];
    items.push({
      id: row.id,
      name: row.name,
      lat: point.lat,
      lng: point.lng,
      address: row.address,
      company: row.company,
      status: aggregateStatus(stationChargers),
      chargerCount: stationChargers.length,
      availableCount: stationChargers.filter((c) => countsAsAvailable(c.status)).length,
      fastChargerCount: stationChargers.filter(
        (c) => c.connector_type !== undefined && isFastConnector(c.connector_type),
      ).length,
      plugs: summarizePlugs(stationChargers),
      tags: row.tags,
      lastActivityAt: lastActivity.get(row.id) ?? null,
    });
  }
  return items;
}

// Never rejects — returns [] on any failure. Callers parallelize this with
// other queries and rely on it not shadowing their error paths.
async function fetchChargers(
  supabase: SupabaseLike,
  stationIds: string[],
): Promise<ChargerStatusRow[]> {
  if (stationIds.length === 0) return [];
  try {
    const resp = await supabase
      .from("chargers_with_status")
      .select("id, station_id, status, connector_type, power_kw")
      .in("station_id", stationIds);
    if (resp.error) {
      console.warn("chargers_with_status query failed; rendering stations as 'unknown'", {
        cause: resp.error,
      });
      return [];
    }
    return (resp.data ?? []) as ChargerStatusRow[];
  } catch (cause) {
    console.warn("chargers_with_status query threw; rendering stations as 'unknown'", { cause });
    return [];
  }
}

async function fetchLastActivity(
  supabase: SupabaseLike,
  stationIds: string[],
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (stationIds.length === 0) return result;
  try {
    const resp = await supabase.rpc("stations_last_activity", { p_station_ids: stationIds });
    if (resp.error) {
      console.warn("stations_last_activity RPC failed; rendering freshness as 'no data'", {
        cause: resp.error,
      });
      return result;
    }
    const rows = (resp.data ?? []) as Array<{ station_id: string; last_activity_at: string }>;
    for (const row of rows) {
      result.set(row.station_id, row.last_activity_at);
    }
    return result;
  } catch (cause) {
    console.warn("stations_last_activity threw; rendering freshness as 'no data'", { cause });
    return result;
  }
}

export function summarizePlugs(chargers: ChargerStatusRow[]): PlugSummary[] {
  const byType = new Map<ConnectorType, number>();
  for (const c of chargers) {
    const prev = byType.get(c.connector_type);
    if (prev === undefined || c.power_kw > prev) byType.set(c.connector_type, c.power_kw);
  }
  const out: PlugSummary[] = [];
  for (const [connector_type, power_kw] of byType) {
    out.push({ connector_type, power_kw });
  }
  return sortPlugGroups(out);
}

// Optimistic-default policy (mirrors backend _aggregate_status): a charger
// with no live report ("unknown" — last report expired past the 2h TTL) reads
// as available, not down. The StatusFreshnessBadge surfaces the "no recent
// reports" caveat separately. Only an explicit "offline" report marks down.
// Single source of the rule so the pill, marker, and rollup can't drift.
export const countsAsAvailable = (status: ChargerStatus): boolean =>
  status === "available" || status === "unknown";

export function aggregateStatus(chargers: ChargerStatusRow[]): StationStatus {
  if (chargers.length === 0) return "available";
  if (chargers.some((c) => countsAsAvailable(c.status))) return "available";
  if (chargers.some((c) => c.status === "waiting" || c.status === "charging")) return "waiting";
  return "offline";
}

export async function getById(id: string): Promise<StationDetail | null> {
  const supabase = getSupabase() as unknown as SupabaseLike;
  // Kick off side-fetches in parallel, but await the station row first so a
  // side-fetch rejection can't shadow the descriptive "station fetch failed"
  // error path. Both helpers swallow their own errors internally.
  const chargersPromise = fetchChargers(supabase, [id]);
  const lastActivityPromise = fetchLastActivity(supabase, [id]);
  const resp = await supabase
    .from("stations")
    .select(
      "id, name, location, address, photo_url, photo_status, pricing_info, company, payment_note, verified, tags",
    )
    .eq("id", id)
    .maybeSingle();
  if (resp.error) {
    throw new Error("station fetch failed", { cause: resp.error });
  }
  if (!resp.data) return null;
  const row = resp.data as StationRow;
  const point = parseLocation(row.location);
  if (point === null) {
    // A row exists but has corrupt geometry — surface as an error so it
    // hits the QueryCache logger; otherwise the route renders a 404 and
    // the data corruption goes unnoticed.
    throw new Error("station has invalid location", {
      cause: { id: row.id, location: row.location },
    });
  }
  const [chargers, lastActivity] = await Promise.all([chargersPromise, lastActivityPromise]);
  return {
    id: row.id,
    name: row.name,
    lat: point.lat,
    lng: point.lng,
    address: row.address,
    photoUrl: row.photo_url,
    photoStatus: row.photo_status,
    pricingInfo: row.pricing_info,
    company: row.company,
    paymentNote: row.payment_note ?? null,
    verified: row.verified,
    tags: row.tags ?? [],
    chargers: chargers.map((c) => ({
      connector_type: c.connector_type,
      power_kw: c.power_kw,
      status: c.status,
    })),
    status: aggregateStatus(chargers),
    lastActivityAt: lastActivity.get(id) ?? null,
  };
}

export function parseLocation(loc: StationRow["location"]): { lat: number; lng: number } | null {
  if (loc && typeof loc === "object" && "coordinates" in loc) {
    const [lng, lat] = loc.coordinates;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  }
  if (typeof loc !== "string") return null;

  const wktMatch = loc.match(/^POINT\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/);
  if (wktMatch) {
    const lng = Number(wktMatch[1]);
    const lat = Number(wktMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  }

  // PostGIS EWKB hex: 1 byte order + 4 type + 4 SRID + 8 lng + 8 lat = 25 bytes (50 hex chars).
  // PostgREST returns this whenever a geography column is selected without ST_AsText.
  if (/^[0-9a-fA-F]{50}$/.test(loc)) {
    const buf = new Uint8Array(25);
    for (let i = 0; i < 25; i++) buf[i] = parseInt(loc.substring(i * 2, i * 2 + 2), 16);
    const view = new DataView(buf.buffer);
    const littleEndian = view.getUint8(0) === 1;
    const lng = view.getFloat64(9, littleEndian);
    const lat = view.getFloat64(17, littleEndian);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  return null;
}
